// WorkflowEngine types — mirrors backend GraphQL schema
// When GQL codegen runs, generated types from #graphql/supergraphTypes replace these.

// Core enums — values match backend WorkflowType.status strings
export enum WorkflowStatus {
  OPEN = "OPEN", // brand new, no tasks started yet
  IN_PROGRESS = "IN_PROGRESS", // at least one task in progress
  PENDING_REVIEW = "PENDING_REVIEW", // tasks awaiting internal approval
  PENDING_EXTERNAL_APPROVAL = "PENDING_EXTERNAL_APPROVAL", // waiting on GP / external party
  BLOCKED = "BLOCKED",
  CLOSED = "CLOSED", // all blocking tasks done
  CANCELLED = "CANCELLED",
  RETURNED = "RETURNED",
}

// Values match backend TaskType.status (computed from executionState + resolution)
export enum TaskStepStatus {
  NOT_STARTED = "NOT_STARTED", // OPEN or PENDING execution state
  IN_PROGRESS = "IN_PROGRESS",
  PENDING_REVIEW = "PENDING_REVIEW", // IN_REVIEW execution state
  EXTERNAL_APPROVAL = "EXTERNAL_APPROVAL", // WAITING execution state
  BLOCKED = "BLOCKED",
  COMPLETED = "COMPLETED", // CLOSED + COMPLETED resolution
  SKIPPED = "SKIPPED", // CLOSED + SKIPPED/N_A resolution
  RETURNED = "RETURNED", // task was rejected and sent back to maker
}

// GP-facing (GPX) status — coarser than TaskStepStatus, used in GPX portal
export enum GpxStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  IN_PROGRESS_RETURNED = "IN_PROGRESS_RETURNED",
  READY_FOR_REVIEW = "READY_FOR_REVIEW",
  COMPLETE = "COMPLETE",
}

export enum TaskType {
  DATA_COLLECTION = "DATA_COLLECTION",
  MAKER_CHECKER = "MAKER_CHECKER",
  MULTI_PARTY_APPROVAL = "MULTI_PARTY_APPROVAL",
  EXTERNAL_APPROVAL = "EXTERNAL_APPROVAL",
  DOCUMENT_UPLOAD = "DOCUMENT_UPLOAD",
}

export enum SubtaskItemType {
  CHECKBOX = "CHECKBOX",
  CHECKBOX_GROUP = "CHECKBOX_GROUP",
  CHECKBOX_WITH_INPUT = "CHECKBOX_WITH_INPUT",
  RADIO_GROUP = "RADIO_GROUP",
  INPUT_FIELD = "INPUT_FIELD",
  TEXTAREA = "TEXTAREA",
  DOCUMENT_UPLOAD = "DOCUMENT_UPLOAD",
  ATTESTATION = "ATTESTATION",
  REPEATABLE_INPUT = "REPEATABLE_INPUT",
  NUMBER = "NUMBER",
  CURRENCY = "CURRENCY",
  DROPDOWN = "DROPDOWN",
}

export interface WorkflowInstanceRow {
  id: number;
  /** Underlying source-entity id (engine workflow id). `id` is the unified-row id
   *  used for grid identity; `sourceId` is what the detail/documents queries key on. */
  sourceId: number;
  title: string;
  templateName: string | null;
  /** "GLOBAL" (built from the template) | "ARENA" (arena override) | null (Jira/unresolved). */
  definitionScope: string | null;
  definitionVersionNumber: number | null;
  workflowType: string;
  status: string;
  currentStepName: string | null;
  currentAssignee: { id: string; displayName: string } | null;
  clientId: string | null;
  clientName: string | null;
  entityId: string | null;
  entityName: string | null;
  client: { id: string; name: string } | null;
  entity: { id: string; name: string } | null;
  createdAt: string | null;
  dueDate: string | null;
  slaStatus: "on_track" | "warning" | "critical" | "breached";
  progress: { completed: number; total: number; percentage: number };
  taskSteps: TaskStepSummary[];
  updatedAt: string | null;
  source: string | null;
  externalRefId: string | null;
  faPreparer: string | null;
  faReviewer: string | null;
  isPreparer: string | null;
  isReviewer: string | null;
  requestedBy: string | null;
  statusCategory: string | null;
}

export interface TaskStepSummary {
  id: number;
  stepOrder: number;
  name: string;
  status: string;
  gpxStatus: string;
  executionState: string;
  taskType: TaskType | null;
  responsibleParty?: string | null;
  assignmentValue?: string | null;
  assignee?: { id: string; displayName: string } | null;
  dueDate?: string | null;
}

export interface WorkflowTableRow {
  id: string;
  path: string[];
  isParentRow: boolean;
  displayName: string;
  workflowId?: string;
  templateName?: string;
  definitionScope?: string | null;
  definitionVersionNumber?: number | null;
  workflowType?: string;
  clientName?: string;
  clientId?: string;
  entityName?: string;
  progress?: { completed: number; total: number; percentage: number };
  currentStepName?: string;
  currentStepTaskId?: string;
  createdAt?: string;
  slaStatus?: "on_track" | "warning" | "critical" | "breached";
  updatedAt?: string | null;
  source?: string | null;
  externalRefId?: string | null;
  faPreparer?: string | null;
  faReviewer?: string | null;
  isPreparer?: string | null;
  isReviewer?: string | null;
  requestedBy?: string | null;
  statusCategory?: string | null;
  assignee?: { id: string; displayName: string } | null;
  dueDate?: string | null;
  status?: string;
  /** GP-facing coarse status for child task rows — rendered in the Status/Status Category column on GPX. */
  gpxStatus?: string | null;
  showIdSubtitle?: boolean;
  stepOrder?: number;
  taskType?: string;
  rawTaskId?: string;
  isJiraPlaceholder?: boolean;
  isJiraLoading?: boolean;
}

export type UserSummary = { id: string; displayName: string };

export interface TaskStepDetail extends TaskStepSummary {
  subtasks: SubtaskItem[];
  mirroredSubtasks?: SubtaskItem[];
  assignee: UserSummary | null;
  maker: UserSummary | null;
  checker: UserSummary | null;
  availableActions: ActionConfig[];
  activityLog: ActivityEvent[];
  nodeKind?: string;
  nodeKey?: string;
  requiresGpApproval?: boolean;
}

export interface SubtaskItem {
  id: number;
  itemType: SubtaskItemType;
  label: string;
  helpText?: string;
  isCompleted?: boolean;
  isRequired?: boolean;
  isCustom?: boolean;
  value?: string | boolean;
  options?: string[];
  conditional_children?: Record<string, SubtaskItem[]>;
  config?: Record<string, unknown>;
  dataType?: string;
  uiSchema?: Record<string, unknown>;
}

export interface ActionConfig {
  action: string;
  label: string;
  variant: "primary" | "neutral" | "danger";
  requiresConfirmation?: boolean;
}

export interface ButtonState extends ActionConfig {
  disabled: boolean;
}

export interface ActivityEvent {
  id: number;
  occurredAt: string;
  actorName: string | null;
  actorId: number | null;
  action: string;
  note: string | null;
  metaData: Record<string, unknown> | null;
  taskId: number | null;
  workflowId: number;
}

export interface WorkflowDocument {
  id: number;
  fileName: string;
  fileUrl: string;
  uploadedBy: string;
  visibility: "INTERNAL" | "EXTERNAL";
  taskStepId?: number;
  createdAt: string;
}

/**
 * Canonical query-filter shape passed to useWorkflowInstances.
 * Built by merging the tab-level constraint (e.g. "assigned to me") with
 * whatever the user has set in the AG Grid column-filter UI.
 */
export interface WorkflowQueryFilters {
  statuses?: string[];
  workflowTypes?: string[];
  clientIds?: string[];
  assignedToUserIds?: string[];
  assignedToCurrentUser?: boolean;
  dueDateBefore?: string;
  dueDateAfter?: string;
}

/** A single option entry returned by workflowFilterOptions */
export interface WorkflowFilterOption {
  value: string;
  displayName: string | null;
}

/**
 * Filter-option arrays passed to getWorkflowColumnDefs to populate
 * agSetColumnFilter dropdowns with server-side distinct values.
 */
export interface WorkflowColumnFilterOptions {
  statuses?: WorkflowFilterOption[];
  workflowTypes?: WorkflowFilterOption[];
  clients?: WorkflowFilterOption[];
  assignees?: WorkflowFilterOption[];
}
export enum TabType {
  Tasks = "tasks",
  Documents = "documents",
  Comments = "comments",
}

export type CommentAuthor = {
  name: string;
  avatar: string | null;
  role: string | null;
};

/**
 * The body of a comment can be either an Atlassian Document Format (ADF) JSON
 * payload (matches the future `WorkflowInstance.comments` GraphQL contract) or
 * raw sanitized HTML (produced by the rich text editor when a user posts a new
 * comment from the UI). The renderer handles both shapes.
 */
export type CommentBody = { kind: "adf"; document: unknown } | { kind: "html"; html: string };

export type WorkflowComment = {
  id: string;
  subject?: string | null;
  body: CommentBody;
  isInternal: boolean;
  parentCommentId: string | null;
  author: CommentAuthor;
  createdAt: string;
};

export type PostCommentInput = {
  body: string;
  isInternal: boolean;
  parentCommentId: string | null;
};

export type WorkflowDetailDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowTitle: string;
  comments: WorkflowComment[];
  loading?: boolean;
  error?: Error | null;
  onPostComment: (input: PostCommentInput) => void | Promise<void>;
  currentUser?: CommentAuthor;
};
